import { useCallback, useEffect } from 'react';
import { Column, PulseCard, Row, Text } from '~/commonComponents';
import { useToggleShortlistCandidate } from '~/domain/PermanentJobs';
import { useToggle } from '~/hooks';
import { BookmarkOutlineIcon, BookmarkSolidIcon } from '~/images/icons';
import { Colors } from '~/themes/colors';

interface IShortlistProfessionalProps {
  isShortlisted: boolean;
  professional: {
    firstName: string;
    id: string;
  };
}

export const ShortlistProfessional = ({
  isShortlisted: inputIsShortlisted,
  professional,
}: IShortlistProfessionalProps) => {
  const [isShortlisted, controls] = useToggle(inputIsShortlisted);

  const { mutate: doShortlistCandidate } = useToggleShortlistCandidate({
    candidateId: professional.id,
    isShortlisted,
  });

  useEffect(() => {
    controls.set(inputIsShortlisted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputIsShortlisted]);

  const handleShortlistClick = useCallback(() => {
    doShortlistCandidate();
    setTimeout(() => {
      controls.setOn();
    });
  }, [controls, doShortlistCandidate]);

  const handleUndoShortlistClick = useCallback(() => {
    doShortlistCandidate();
    setTimeout(() => {
      controls.setOff();
    });
  }, [controls, doShortlistCandidate]);

  return (
    <PulseCard
      ActionComponent={() =>
        isShortlisted ? (
          <Column alignItems="center">
            <Row
              alignItems="center"
              gap={8}
              justifyContent="center"
              paddingBlock={11}
              paddingInline={24}
            >
              <BookmarkSolidIcon />
              <Text bold color={Colors.secondary_500} fontSize={18}>
                Shortlisted
              </Text>
            </Row>
            <Text id="btn-undo-shortlist" onClick={handleUndoShortlistClick}>
              Undo Shortlist
            </Text>
          </Column>
        ) : (
          <Row
            alignItems="center"
            backgroundColor={Colors.secondary_500}
            borderRadius={25}
            className="shadow"
            cursor="pointer"
            gap={8}
            justifyContent="center"
            paddingBlock={11}
            paddingInline={24}
            width={262}
            style={{
              boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.15), 0 6px 10px 2px rgba(0, 0, 0, 0.15)',
            }}
            onClick={handleShortlistClick}
          >
            <BookmarkOutlineIcon color={Colors.white} />
            <Text bold color={Colors.white} fontSize={18}>
              Shortlist Professional
            </Text>
          </Row>
        )
      }
      isItalic={!isShortlisted}
      ComponentProps={{
        BoxProps: isShortlisted
          ? {
              backgroundColor: '#F2FBFB',
              borderColor: Colors.secondary_500,
              borderWidth: 1.5,
            }
          : {},
      }}
      description={
        isShortlisted ? (
          `${professional.firstName} has been shortlisted as a Candidate for this job.`
        ) : (
          <>
            Shortlisted professionals will be displayed at top in the Candidates tab
            <br />
            of Manage Jobs.
          </>
        )
      }
      title={isShortlisted ? 'Congrats!' : 'Would you like to shortlist this professional?'}
    />
  );
};
